import { motion } from 'framer-motion';

export type CheckInRank = 'first' | 'regular' | 'deep_diver';

interface RankConfig {
  label: string;
  sublabel: string;
  icon: string;
  color: string;
  glowColor: string;
  bgGradient: string;
  borderGradient: string;
  particleColor: string;
  minVisits: number;
}

export const RANK_CONFIG: Record<CheckInRank, RankConfig> = {
  first: {
    label: '初来場',
    sublabel: 'FIRST VISIT',
    icon: 'ri-seedling-line',
    color: '#4ade80',
    glowColor: 'rgba(74,222,128,0.4)',
    bgGradient: 'linear-gradient(135deg, rgba(74,222,128,0.15), rgba(16,185,129,0.08))',
    borderGradient: 'linear-gradient(135deg, rgba(74,222,128,0.6), rgba(16,185,129,0.3))',
    particleColor: '#4ade80',
    minVisits: 1,
  },
  regular: {
    label: '常連',
    sublabel: 'REGULAR',
    icon: 'ri-fire-line',
    color: '#fb923c',
    glowColor: 'rgba(251,146,60,0.4)',
    bgGradient: 'linear-gradient(135deg, rgba(251,146,60,0.15), rgba(234,88,12,0.08))',
    borderGradient: 'linear-gradient(135deg, rgba(251,146,60,0.6), rgba(234,88,12,0.3))',
    particleColor: '#fb923c',
    minVisits: 5,
  },
  deep_diver: {
    label: 'DEEP DIVER',
    sublabel: 'DEEP DIVER',
    icon: 'ri-vip-crown-fill',
    color: '#22d3ee',
    glowColor: 'rgba(34,211,238,0.5)',
    bgGradient: 'linear-gradient(135deg, rgba(34,211,238,0.2), rgba(168,85,247,0.15))',
    borderGradient: 'linear-gradient(135deg, #22d3ee, #a855f7)',
    particleColor: '#22d3ee',
    minVisits: 10,
  },
};

export function getRank(visitCount: number): CheckInRank {
  if (visitCount >= 10) return 'deep_diver';
  if (visitCount >= 5) return 'regular';
  return 'first';
}

interface CheckInRankBadgeProps {
  visitCount: number;
  shopName: string;
}

export function CheckInRankBadge({ visitCount, shopName }: CheckInRankBadgeProps) {
  const rank = getRank(visitCount);
  const config = RANK_CONFIG[rank];

  // 次のランクまでの進捗
  const nextRankVisits = rank === 'first' ? 5 : rank === 'regular' ? 10 : null;
  const prevRankVisits = rank === 'first' ? 1 : rank === 'regular' ? 5 : 10;
  const progress = nextRankVisits
    ? Math.min(((visitCount - prevRankVisits) / (nextRankVisits - prevRankVisits)) * 100, 100)
    : 100;

  return (
    <motion.div
      className="w-full max-w-sm"
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.4, ease: 'easeOut' }}
    >
      {/* セクションラベル */}
      <div className="flex items-center gap-golden-sm mb-golden-sm">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-white/50 text-[10px] font-bold tracking-[0.4em]">
          YOUR RANK
        </span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* メインバッジカード */}
      <div
        className="rounded-golden-lg p-px relative overflow-hidden"
        style={{ 
          background: config.borderGradient,
          boxShadow: rank === 'deep_diver' 
            ? `0 0 20px ${config.glowColor}, 0 0 40px ${config.glowColor}, 0 8px 32px rgba(0,0,0,0.6), inset 0 0 15px ${config.glowColor}`
            : `0 0 15px ${config.glowColor}, 0 8px 24px rgba(0,0,0,0.5)`,
        }}
      >
        {/* DEEP DIVERのみ追加グロー */}
        {rank === 'deep_diver' && (
          <motion.div
            className="absolute inset-0 rounded-golden-lg"
            style={{ background: config.borderGradient, opacity: 0.3 }}
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 2.5, repeat: Infinity }}
          />
        )}

        <div
          className="relative rounded-golden-lg p-golden-lg flex flex-col items-center gap-golden-sm backdrop-blur-xl"
          style={{ 
            background: config.bgGradient,
          }}
        >
          {/* アイコン */}
          <div className="relative">
            {rank === 'deep_diver' && (
              <motion.div
                className="absolute inset-0 rounded-full blur-xl"
                style={{ background: config.glowColor }}
                animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0.9, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
            <motion.div
              className="relative w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)`,
                border: `2px solid ${config.color}60`,
                boxShadow: rank === 'deep_diver'
                  ? `0 0 30px ${config.glowColor}, 0 0 50px ${config.glowColor}, inset 0 0 20px ${config.glowColor}`
                  : `0 0 20px ${config.glowColor}, inset 0 0 10px ${config.glowColor}`,
              }}
              animate={
                rank === 'deep_diver'
                  ? { 
                      boxShadow: [
                        `0 0 20px ${config.glowColor}, 0 0 40px ${config.glowColor}, inset 0 0 15px ${config.glowColor}`, 
                        `0 0 40px ${config.glowColor}, 0 0 70px ${config.glowColor}, inset 0 0 30px ${config.glowColor}`, 
                        `0 0 20px ${config.glowColor}, 0 0 40px ${config.glowColor}, inset 0 0 15px ${config.glowColor}`
                      ] 
                    }
                  : {}
              }
              transition={{ duration: 2, repeat: Infinity }}
            >
              <motion.i
                className={`${config.icon} text-4xl`}
                style={{ 
                  color: config.color,
                  filter: `drop-shadow(0 0 8px ${config.glowColor})`,
                }}
                animate={rank === 'deep_diver' ? { 
                  filter: [
                    `drop-shadow(0 0 5px ${config.glowColor})`,
                    `drop-shadow(0 0 15px ${config.glowColor})`,
                    `drop-shadow(0 0 5px ${config.glowColor})`
                  ]
                } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.div>
          </div>

          {/* ランク名 */}
          <div className="text-center">
            <motion.p
              className="text-2xl font-black tracking-widest"
              style={{ 
                color: config.color, 
                textShadow: `0 0 20px ${config.glowColor}, 0 0 40px ${config.glowColor}`,
              }}
              animate={
                rank === 'deep_diver'
                  ? { 
                      textShadow: [
                        `0 0 10px ${config.glowColor}, 0 0 30px ${config.glowColor}`, 
                        `0 0 25px ${config.glowColor}, 0 0 60px ${config.glowColor}`, 
                        `0 0 10px ${config.glowColor}, 0 0 30px ${config.glowColor}`
                      ] 
                    }
                  : {}
              }
              transition={{ duration: 2, repeat: Infinity }}
            >
              {config.label}
            </motion.p>
            <p className="text-white/40 text-[10px] tracking-[0.3em] mt-1">
              {config.sublabel}
            </p>
          </div>

          {/* チェックイン回数 */}
          <div
            className="flex items-center gap-golden-xs px-golden-sm py-golden-xs rounded-full"
            style={{
              background: `${config.color}15`,
              border: `1px solid ${config.color}40`,
              boxShadow: `0 0 15px ${config.glowColor}`,
            }}
          >
            <i className="ri-map-pin-2-fill text-sm" style={{ color: config.color }} />
            <span className="text-white/80 text-sm font-bold tracking-wider">
              {visitCount}
              <span className="text-white/40 text-xs font-normal ml-1">回来場</span>
            </span>
          </div>

          {/* 店舗名 */}
          <p className="text-white/30 text-[10px] tracking-widest text-center">
            @ {shopName}
          </p>

          {/* 進捗バー（DEEP DIVERでない場合） */}
          {nextRankVisits && (
            <div className="w-full">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-white/30 text-[10px] tracking-wider">NEXT RANK</span>
                <span className="text-[10px] font-bold" style={{ color: config.color }}>
                  {visitCount} / {nextRankVisits}
                </span>
              </div>
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ 
                    background: `linear-gradient(90deg, ${config.color}, ${config.color}80)`,
                    boxShadow: `0 0 10px ${config.glowColor}`,
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 1, delay: 0.8, ease: 'easeOut' }}
                />
              </div>
              <p className="text-white/20 text-[10px] mt-1.5 text-center">
                あと{nextRankVisits - visitCount}回で
                <span className="font-bold" style={{ color: config.color }}>
                  {nextRankVisits >= 10 ? ' DEEP DIVER' : ' 常連'}
                </span>
                に昇格
              </p>
            </div>
          )}

          {/* DEEP DIVERの場合：最高ランク表示 */}
          {rank === 'deep_diver' && (
            <motion.div
              className="w-full text-center"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <p 
                className="text-[10px] tracking-widest" 
                style={{ 
                  color: config.color,
                  textShadow: `0 0 15px ${config.glowColor}`,
                }}
              >
                ✦ この空間の最深部に到達した者 ✦
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* ランク一覧（小さく表示） */}
      <div className="mt-golden-sm flex gap-golden-xs justify-center">
        {(Object.entries(RANK_CONFIG) as [CheckInRank, RankConfig][]).map(([key, cfg]) => (
          <div
            key={key}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{
              background: key === rank ? `${cfg.color}20` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${key === rank ? cfg.color + '60' : 'rgba(255,255,255,0.08)'}`,
              boxShadow: key === rank ? `0 0 10px ${cfg.glowColor}` : 'none',
            }}
          >
            <i
              className={`${cfg.icon} text-[10px]`}
              style={{ color: key === rank ? cfg.color : 'rgba(255,255,255,0.3)' }}
            />
            <span
              className="text-[9px] font-bold tracking-wider"
              style={{ color: key === rank ? cfg.color : 'rgba(255,255,255,0.3)' }}
            >
              {cfg.label}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
